import { batch } from 'react-redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { handleResponse } from '../../Helper';
import { configurationApi, graphicApi } from '../../api';
import { RedlineConfigValues, TupleTyped } from '../../model/CommonModel';
import { ProfileSetting } from '../../model/ConfigurationModel';
import {
	Coordinate,
	FloatingWindowsVisibility,
	GraphicActionType,
	GraphicAlarmPermission,
	GraphicDirectoryInfo,
	GraphicPageMode,
	MapsState,
	SpaceObjectSerialize,
} from '../../model/GraphicModel';
import { RootReducer } from '../rootReducer';
import { ObjectLibraryTreeNode } from './../../model/GraphicModel';
import { GraphicActionTypes } from './actionTypes';

export interface SetSpaceObjectData {
	type: GraphicActionTypes.SET_SPACE_OBJECT_DATA;
	payload: Partial<SpaceObjectSerialize>;
}

export interface SetMapHasChanged {
	type: GraphicActionTypes.MAP_HAS_CHANGED;
	payload: boolean;
}

type GraphicAlarmPermissions = {
	type: GraphicActionTypes.SET_ALARM_PERMISSIONS;
	payload: GraphicAlarmPermission;
};

type SetErrorMessage = {
	type: GraphicActionTypes.SET_ERROR_MESSAGE;
	payload: string;
};

type SetErrorMessages = {
	type: GraphicActionTypes.SET_ERROR_MESSAGES;
	payload: TupleTyped<number, string>[];
};

type SetDropDataNodes = {
	type: GraphicActionTypes.SET_DROP_DATA_NODES;
	payload: ObjectLibraryTreeNode[];
};

type DisableMenuOpts = {
	type: GraphicActionTypes.DISABLE_MENU_OPTS;
	payload: boolean;
};

type SetCurrentRedlineConfigValues = {
	type: GraphicActionTypes.SET_CURRENT_REDLINE_CONFIG_VALUES;
	payload: RedlineConfigValues;
};

type SwitchFloatingWindowVisibility = {
	type: GraphicActionTypes.SWITCH_FLOATING_WINDOW_VISIBILITY;
	payload: Partial<FloatingWindowsVisibility>;
};

type SetMapMode = {
	type: GraphicActionTypes.SET_MAP_MODE;
	payload: GraphicPageMode;
};

type SetMapDirectoryData = {
	type: GraphicActionTypes.SET_MAP_DIRECTORY_DATA;
	payload: Partial<GraphicDirectoryInfo>;
};

type SetGraphicActionType = {
	type: GraphicActionTypes.SET_GRAPHIC_ACTION_TYPE;
	payload: GraphicActionType;
};

type SaveGraphicPreferences = {
	type: GraphicActionTypes.SAVE_GRAPHIC_PREFERENCES;
};

type SaveGraphicPreferencesSuccess = {
	type: GraphicActionTypes.SAVE_GRAPHIC_PREFERENCES_SUCCESS;
};

type SetLastObjectLibraryCoordinates = {
	type: GraphicActionTypes.SET_LAST_OBJECT_LIBRARY_COORDINATES;
	payload: Coordinate;
};

type SetLastPlottedDevicesCoordinates = {
	type: GraphicActionTypes.SET_LAST_PLOTTED_DEVICES_COORDINATES;
	payload: Coordinate;
};

type SetLastAlarmQueueCoordinates = {
	type: GraphicActionTypes.SET_LAST_ALARM_QUEUE_COORDINATES;
	payload: Coordinate;
};

export type GraphicAction =
	| SetMapMode
	| SetMapDirectoryData
	| SetGraphicActionType
	| SetSpaceObjectData
	| SetMapHasChanged
	| GraphicAlarmPermissions
	| SetErrorMessage
	| SetErrorMessages
	| SetDropDataNodes
	| DisableMenuOpts
	| SetCurrentRedlineConfigValues
	| SwitchFloatingWindowVisibility
	| SaveGraphicPreferences
	| SaveGraphicPreferencesSuccess
	| SetLastObjectLibraryCoordinates
	| SetLastPlottedDevicesCoordinates
	| SetLastAlarmQueueCoordinates;

export const setSpaceObjectDataAction = (payload: Partial<SpaceObjectSerialize>): SetSpaceObjectData => ({
	type: GraphicActionTypes.SET_SPACE_OBJECT_DATA,
	payload,
});
export const setMapHasChangedAction = (payload: boolean): SetMapHasChanged => ({
	type: GraphicActionTypes.MAP_HAS_CHANGED,
	payload,
});
export const setGraphicAlarmPermissions = (payload: GraphicAlarmPermission): GraphicAlarmPermissions => ({
	type: GraphicActionTypes.SET_ALARM_PERMISSIONS,
	payload,
});

export const setErrorMessageAction = (payload: string): SetErrorMessage => ({
	type: GraphicActionTypes.SET_ERROR_MESSAGE,
	payload,
});
const setErrorMessagesAction = (payload: TupleTyped<number, string>[]): SetErrorMessages => ({
	type: GraphicActionTypes.SET_ERROR_MESSAGES,
	payload,
});
export const setDropDataNodesAction = (payload: ObjectLibraryTreeNode[]): SetDropDataNodes => ({
	type: GraphicActionTypes.SET_DROP_DATA_NODES,
	payload,
});
export const disableMenuOptsAction = (payload: boolean): DisableMenuOpts => ({
	type: GraphicActionTypes.DISABLE_MENU_OPTS,
	payload,
});
export const setCurrentRedlineConfigValuesAction = (payload: RedlineConfigValues): SetCurrentRedlineConfigValues => ({
	type: GraphicActionTypes.SET_CURRENT_REDLINE_CONFIG_VALUES,
	payload,
});
const setMapModeAction = (payload: GraphicPageMode): SetMapMode => ({
	type: GraphicActionTypes.SET_MAP_MODE,
	payload,
});
export const setMapDirectoryDataAction = (payload: Partial<GraphicDirectoryInfo>): SetMapDirectoryData => ({
	type: GraphicActionTypes.SET_MAP_DIRECTORY_DATA,
	payload,
});
export const setGraphicActionType = (payload: GraphicActionType): SetGraphicActionType => ({
	type: GraphicActionTypes.SET_GRAPHIC_ACTION_TYPE,
	payload,
});
export const switchFloatingWindowVisibilityAction = (payload: Partial<FloatingWindowsVisibility>): SwitchFloatingWindowVisibility => ({
	type: GraphicActionTypes.SWITCH_FLOATING_WINDOW_VISIBILITY,
	payload,
});
const saveGraphicPreferencesAction = (): SaveGraphicPreferences => ({
	type: GraphicActionTypes.SAVE_GRAPHIC_PREFERENCES,
});
const saveGraphicPreferencesSuccessAction = (): SaveGraphicPreferencesSuccess => ({
	type: GraphicActionTypes.SAVE_GRAPHIC_PREFERENCES_SUCCESS,
});
export const setLastObjectLibraryCoordinatesAction = (payload: Coordinate): SetLastObjectLibraryCoordinates => ({
	payload,
	type: GraphicActionTypes.SET_LAST_OBJECT_LIBRARY_COORDINATES,
});
export const setLastPlottedDevicesCoordinatesAction = (payload: Coordinate): SetLastPlottedDevicesCoordinates => ({
	payload,
	type: GraphicActionTypes.SET_LAST_PLOTTED_DEVICES_COORDINATES,
});
export const setLastAlarmQueueCoordinatesAction = (payload: Coordinate): SetLastAlarmQueueCoordinates => ({
	payload,
	type: GraphicActionTypes.SET_LAST_ALARM_QUEUE_COORDINATES,
});

// ThunkActions
export const setErrorMessages = (): ThunkAction<Promise<void>, { graphicReducer: MapsState }, {}, GraphicAction> => {
	return async (dispatch: ThunkDispatch<{}, {}, GraphicAction>): Promise<void> => {
		graphicApi.getErrorMessages().then(response => {
			if (!handleResponse(response)) {
				dispatch(setErrorMessagesAction(response.Entity));
			}
		});
	};
};
export const setMapMode = (
	payload: GraphicPageMode,
	firstLoad: boolean = false
): ThunkAction<Promise<void>, { graphicReducer: MapsState }, {}, GraphicAction> => {
	return async (dispatch: ThunkDispatch<{}, {}, GraphicAction>) => {
		batch(() => {
			if (!firstLoad) {
				dispatch(saveGraphicPreferences());
			}
			dispatch(setMapModeAction(payload));
		});
	};
};
export const saveGraphicPreferences = (): ThunkAction<Promise<void>, { mapReducer: MapsState }, {}, GraphicAction> => {
	return async (dispatch: ThunkDispatch<{}, {}, GraphicAction>, getState: () => RootReducer) => {
		const rootReducer: RootReducer = getState();
		const {
			mapReducer: {
				viewMode,
				lastObjectDirectoryCoordinates,
				lastPlottedDevicesCoordinates,
				lastAlarmQueueCoordinates,
				floatingWindowsVisibility: { devices, alarmQueue, plottedDevices, objectProperties, quickAccess, birdEye },
			},
		} = rootReducer;

		if (viewMode === GraphicPageMode.Design) {
			dispatch(saveGraphicPreferencesAction());

			const ObjectId: number = 5;
			const settings: ProfileSetting[] = [
				{
					Name: 'DevicesWindowPosition',
					ObjectId,
					Value: `${lastObjectDirectoryCoordinates.x},${lastObjectDirectoryCoordinates.y}`,
				},
				{
					Name: 'PlottedDevicesWindowPosition',
					ObjectId,
					Value: `${lastPlottedDevicesCoordinates.x},${lastPlottedDevicesCoordinates.y}`,
				},
				{
					Name: 'AlarmQueueWindowPosition',
					ObjectId,
					Value: `${lastAlarmQueueCoordinates.x},${lastAlarmQueueCoordinates.y}`,
				},
				{
					Name: 'IsVisibleBirdEyeWindow',
					ObjectId,
					Value: birdEye ? 'True' : 'False',
				},
				{
					Name: 'IsVisibleDevicesWindow',
					ObjectId,
					Value: devices ? 'True' : 'False',
				},
				{
					Name: 'IsVisibleAlarmQueueWindow',
					ObjectId,
					Value: alarmQueue ? 'True' : 'False',
				},
				{
					Name: 'IsVisibleObjectPropertiesWindow',
					ObjectId,
					Value: objectProperties ? 'True' : 'False',
				},
				{
					Name: 'IsVisiblePlottedDevicesWindow',
					ObjectId,
					Value: plottedDevices ? 'True' : 'False',
				},
				{
					Name: 'IsVisibleQuickAccessWindow',
					ObjectId,
					Value: quickAccess ? 'True' : 'False',
				},
			];
			configurationApi
				.saveSettingsChanges(settings, [])
				.then(res => {
					if (!handleResponse(res)) {
						dispatch(saveGraphicPreferencesSuccessAction());
					} else {
						dispatch(setErrorMessageAction(res.ErrorMessage));
					}
				})
				.catch(err => {
					dispatch(setErrorMessageAction(err));
				});
		}
	};
};
